"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  deriveLocaleFromPath,
  switchLocalePath,
  type Locale,
} from "@/lib/i18n";
import { getMessages } from "@/lib/messages";

/**
 * EN | FR switcher for Canadian pages. Sets cloudaxe-geo cookie via ?geo=
 * so the proxy honors the chosen locale on subsequent visits.
 */
export function LocaleSwitcher() {
  const pathname = usePathname() || "/";
  const { country, locale } = deriveLocaleFromPath(pathname);
  if (country !== "CA") return null;

  const m = getMessages(locale);
  const enHref = `${switchLocalePath(pathname, "en")}?geo=ca`;
  const frHref = `${switchLocalePath(pathname, "fr")}?geo=ca-fr`;

  const linkClass = (active: boolean) =>
    active
      ? "text-slate-900 font-semibold dark:text-white"
      : "text-slate-500 hover:text-slate-800 dark:text-white/55 dark:hover:text-white";

  return (
    <div
      className="hidden items-center gap-1 rounded-2xl bg-white/40 px-2 py-1 text-xs ring-1 ring-white/40 backdrop-blur-xl md:inline-flex dark:bg-white/10 dark:ring-white/20"
      role="navigation"
      aria-label="Language"
    >
      <Link
        href={enHref}
        className={linkClass(locale === "en")}
        aria-current={locale === "en" ? "true" : undefined}
        hrefLang="en-CA"
        title={m.nav.switchToEnglish}
      >
        {m.nav.langEn}
      </Link>
      <span className="text-slate-400 dark:text-white/30" aria-hidden>
        |
      </span>
      <Link
        href={frHref}
        className={linkClass(locale === "fr")}
        aria-current={locale === "fr" ? "true" : undefined}
        hrefLang="fr-CA"
        title={m.nav.switchToFrench}
      >
        {m.nav.langFr}
      </Link>
    </div>
  );
}

export function localeSwitcherTargets(pathname: string, locale: Locale) {
  return {
    en: `${switchLocalePath(pathname, "en")}?geo=ca`,
    fr: `${switchLocalePath(pathname, "fr")}?geo=ca-fr`,
    active: locale,
  };
}
